import { apiFetch } from "../../../shared/apiClient";
import {
  FACTSET_OAUTH_ERROR_CODES,
  FACTSET_ROUTES,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "../../../shared/factset/factsetConstants";
import type { FetchPreslinkFilesResult } from "../factset/intentBridgeService";
import * as JSZipModule from "jszip";

const PRESLINK_PROCESS_ENDPOINT = "/api/ai/preslink/process-presentation-file";
const PRESLINK_DOWNLOAD_ENDPOINT = "/api/ai/preslink/download";
const DEFAULT_PRESLINK_PROCESS_PROMPT = "Process the PresLink-linked presentation content.";

export interface PreslinkProcessPresentationFile {
  presentation_id: string;
  zip_base64: string;
  file_name: string;
}

export interface PreslinkProcessLinkedFile {
  link_key: string;
  zip_base64: string;
  file_name: string;
}

export interface PreslinkProcessShape {
  shape_id: string;
  link_keys: string[];
}

export interface PreslinkProcessSlide {
  slide_id: string;
  shapes: PreslinkProcessShape[];
}

export interface PreslinkProcessData {
  presentation: PreslinkProcessPresentationFile;
  linked_files: PreslinkProcessLinkedFile[];
  slides: PreslinkProcessSlide[];
}

export interface PreslinkProcessPresentationRequest {
  prompt: string;
  auth_token?: string | null;
  skip_llm: boolean;
  data: PreslinkProcessData;
}

export interface BuildPreslinkProcessRequestOptions {
  prompt?: string | null;
  authToken?: string | null;
  skipLlm?: boolean;
  linkedFileIdentityByLinkKey?: ReadonlyMap<string, string>;
}

interface MinimalZipEntry {
  async(type: "uint8array"): Promise<Uint8Array>;
}

interface MinimalZip {
  files: Record<string, MinimalZipEntry>;
}

interface MinimalJSZip {
  loadAsync(data: string, options: { base64: boolean }): Promise<MinimalZip>;
}

export interface PreslinkProcessSafeLinkedFile {
  link_key: string;
  file_name?: string;
  byteLength: number;
}

export interface PreslinkProcessSafeSummary {
  errors: unknown[];
  download_urls?: unknown;
  data?: {
    presentation?: {
      presentation_id?: string;
      file_name?: string;
      archiveByteLength: number;
    };
    linked_files: PreslinkProcessSafeLinkedFile[];
    slides: PreslinkProcessSlide[];
  };
}

export interface PreslinkProcessOutputLinkedFile {
  link_key: string;
  file_name?: string;
  zip_base64: string;
}

export interface PreslinkProcessResult {
  /** Model-safe summary (byte lengths only — never raw archive bytes). */
  summary: PreslinkProcessSafeSummary;
  /**
   * Raw base64 of the processed presentation (.pptx) so the caller can re-open
   * it into the deck. Kept out of `summary` (and all logging) because it is large.
   */
  processedPresentationBase64?: string;
  processedPresentationFileName?: string;
  /**
   * Raw processed workbook payloads returned by the process API. Used only by
   * the host bridge to materialize versioned files to their source folders.
   */
  processedLinkedFiles?: PreslinkProcessOutputLinkedFile[];
}

export interface PreslinkProcessEvent {
  type: string;
  message?: string;
  code?: string;
  error?: string;
  data?: Record<string, unknown> | null;
  timestamp_ms?: number;
  excel_file?: string | null;
}

export interface PostPreslinkProcessPresentationFileOptions {
  signal?: AbortSignal;
  onEvent?: (event: PreslinkProcessEvent) => void;
  onProgress?: (message: string, event: PreslinkProcessEvent) => void;
}

/** Signals that PresLink needs a fresh per-user FactSet OAuth grant. */
export class PreslinkReauthRequiredError extends Error {
  reauthorizationUrl: string;
  serverErrorCode: string;

  constructor(message: string, reauthorizationUrl: string, serverErrorCode: string) {
    super(message);
    this.name = "PreslinkReauthRequiredError";
    this.reauthorizationUrl = reauthorizationUrl;
    this.serverErrorCode = serverErrorCode;
    Object.setPrototypeOf(this, PreslinkReauthRequiredError.prototype);
  }
}

/**
 * Build a compact, log-safe summary of a process request: counts and byte
 * lengths only, never raw archive bytes or the auth token.
 */
function summarizePreslinkProcessRequest(request: PreslinkProcessPresentationRequest): Record<string, unknown> {
  const linkedFiles = request.data.linked_files ?? [];
  const slides = request.data.slides ?? [];
  return {
    promptLength: request.prompt.length,
    skip_llm: request.skip_llm,
    hasAuthToken: typeof request.auth_token === "string" && request.auth_token.length > 0,
    presentation: {
      presentation_id: request.data.presentation.presentation_id,
      file_name: request.data.presentation.file_name,
      byteLength: base64ByteLength(request.data.presentation.zip_base64),
    },
    linkedFileCount: linkedFiles.length,
    linkedFiles: linkedFiles.slice(0, 20).map((file) => ({
      link_key: file.link_key,
      file_name: file.file_name,
      byteLength: base64ByteLength(file.zip_base64),
    })),
    slideCount: slides.length,
    shapeCount: slides.reduce((count, slide) => count + (slide.shapes?.length ?? 0), 0),
  };
}

/** Trim the caller-supplied prompt, falling back to the default process prompt. */
function normalizePrompt(prompt?: string | null): string {
  const trimmed = typeof prompt === "string" ? prompt.trim() : "";
  return trimmed || DEFAULT_PRESLINK_PROCESS_PROMPT;
}

/** Estimate the decoded byte length of a base64 string without allocating it. */
export function base64ByteLength(base64: string | undefined): number {
  const normalized = (base64 || "").replace(/\s+/g, "");
  if (!normalized) return 0;
  const padding = normalized.endsWith("==") ? 2 : normalized.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((normalized.length * 3) / 4) - padding);
}

/** Narrow an unknown value to a plain (non-array) object record, or null. */
function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Read a trimmed, non-empty string property from a record, or undefined. */
function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * Derive a stable identity for a linked workbook so duplicates that resolve to
 * the same source file collapse to one entry. Prefers the source-folder
 * identity, then the file name, then the raw link key.
 */
function normalizeLinkedFileIdentity(
  fileName: string | undefined,
  linkKey: string,
  sourceIdentity?: string
): string {
  const normalizedFileName = (fileName || "").trim().toLowerCase();
  const normalizedSourceIdentity = (sourceIdentity || "").trim().toLowerCase();
  if (normalizedSourceIdentity) {
    return `source:${normalizedSourceIdentity}|${normalizedFileName || linkKey}`;
  }
  return normalizedFileName || `link_key:${linkKey}`;
}

/** Encode raw bytes to base64, using Node's Buffer when available and a chunked btoa fallback otherwise. */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  const maybeBuffer = (globalThis as unknown as {
    Buffer?: { from(data: Uint8Array): { toString(encoding: "base64"): string } };
  }).Buffer;
  if (maybeBuffer) {
    return maybeBuffer.from(bytes).toString("base64");
  }
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    let chunkBinary = "";
    for (let index = 0; index < chunk.length; index += 1) {
      chunkBinary += String.fromCharCode(chunk[index]);
    }
    binary += chunkBinary;
  }
  return globalThis.btoa(binary);
}

/** Encode an ArrayBuffer to base64. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  return uint8ArrayToBase64(new Uint8Array(buffer));
}

/**
 * Unwrap a single-file Office archive: when `base64` is a zip that merely wraps
 * one Office document (rather than the document's own OPC package), return the
 * inner document's base64. Returns the input unchanged if it already looks like
 * the raw Office package or anything unexpected happens.
 */
async function unwrapSingleOfficeFileArchive(
  base64: string,
  options: { rawEntryPrefix: string; fileExtensionPattern: RegExp }
): Promise<string> {
  if (!base64) return base64;
  try {
    const JSZip = ((JSZipModule as unknown as { default?: MinimalJSZip }).default ??
      (JSZipModule as unknown as MinimalJSZip));
    const zip = await JSZip.loadAsync(base64, { base64: true });
    const entryNames = Object.keys(zip.files);
    if (entryNames.some((name) => name.startsWith(options.rawEntryPrefix))) {
      return base64;
    }
    const officeEntryName = entryNames.find((name) => options.fileExtensionPattern.test(name));
    if (!officeEntryName) return base64;
    const bytes = await zip.files[officeEntryName].async("uint8array");
    return uint8ArrayToBase64(bytes);
  } catch {
    return base64;
  }
}

/**
 * Deduplicate fetched linked workbooks by resolved identity and unwrap each to
 * its raw Office package. Returns the canonical linked-file list plus a map from
 * every raw link key to the canonical link key that survived deduplication.
 */
async function buildCanonicalPreslinkFiles(
  fetched: FetchPreslinkFilesResult,
  linkedFileIdentityByLinkKey?: ReadonlyMap<string, string>
): Promise<{
  linkedFiles: PreslinkProcessLinkedFile[];
  canonicalLinkKeyByRawKey: Map<string, string>;
}> {
  const linkedFileByIdentity = new Map<string, PreslinkProcessLinkedFile>();
  const canonicalLinkKeyByRawKey = new Map<string, string>();

  for (const file of fetched.linkedFiles) {
    const identity = normalizeLinkedFileIdentity(
      file.fileName,
      file.linkKey,
      linkedFileIdentityByLinkKey?.get(file.linkKey)
    );
    const existing = linkedFileByIdentity.get(identity);
    if (existing) {
      canonicalLinkKeyByRawKey.set(file.linkKey, existing.link_key);
      continue;
    }

    const canonicalFile = {
      link_key: file.linkKey,
      zip_base64: await unwrapSingleOfficeFileArchive(file.zipBase64, {
        rawEntryPrefix: "xl/",
        fileExtensionPattern: /\.(xlsx|xlsm|xlsb|xls)$/i,
      }),
      file_name: file.fileName ?? "",
    };
    linkedFileByIdentity.set(identity, canonicalFile);
    canonicalLinkKeyByRawKey.set(file.linkKey, canonicalFile.link_key);
  }

  return {
    linkedFiles: Array.from(linkedFileByIdentity.values()),
    canonicalLinkKeyByRawKey,
  };
}

/** Remap a shape's link keys onto their canonical keys, dropping duplicates. */
function remapShapeLinkKeys(linkKeys: string[], canonicalLinkKeyByRawKey: Map<string, string>): string[] {
  const remapped: string[] = [];
  const seen = new Set<string>();
  for (const linkKey of linkKeys) {
    const canonicalLinkKey = canonicalLinkKeyByRawKey.get(linkKey) ?? linkKey;
    if (seen.has(canonicalLinkKey)) continue;
    seen.add(canonicalLinkKey);
    remapped.push(canonicalLinkKey);
  }
  return remapped;
}

/**
 * Assemble the process-presentation request payload from fetched PresLink files:
 * unwraps the presentation and linked archives, deduplicates linked workbooks,
 * and remaps shape link keys to their canonical keys.
 */
export async function buildPreslinkProcessPresentationRequest(
  fetched: FetchPreslinkFilesResult,
  options: BuildPreslinkProcessRequestOptions = {}
): Promise<PreslinkProcessPresentationRequest> {
  const { linkedFiles, canonicalLinkKeyByRawKey } = await buildCanonicalPreslinkFiles(
    fetched,
    options.linkedFileIdentityByLinkKey
  );
  const request: PreslinkProcessPresentationRequest = {
    prompt: normalizePrompt(options.prompt),
    skip_llm: options.skipLlm ?? false,
    data: {
      presentation: {
        presentation_id: fetched.presentation.presentationId ?? "",
        zip_base64: await unwrapSingleOfficeFileArchive(fetched.presentation.zipBase64, {
          rawEntryPrefix: "ppt/",
          fileExtensionPattern: /\.(pptx|pptm)$/i,
        }),
        file_name: fetched.presentation.fileName ?? "",
      },
      linked_files: linkedFiles,
      slides: fetched.slides.map((slide) => ({
        slide_id: slide.slideId,
        shapes: slide.shapes.map((shape) => ({
          shape_id: shape.shapeId,
          link_keys: remapShapeLinkKeys(shape.linkKeys, canonicalLinkKeyByRawKey),
        })),
      })),
    },
  };

  if (options.authToken !== undefined) {
    request.auth_token = options.authToken;
  }

  return request;
}

/**
 * Reduce a raw process API response to a model-safe summary: byte lengths and
 * structure only, with all raw archive payloads stripped.
 */
export function summarizePreslinkProcessResponse(response: unknown): PreslinkProcessSafeSummary {
  const root = asRecord(response) ?? {};
  const data = asRecord(root.data);
  const presentation = data ? asRecord(data.presentation) : null;
  const linkedFilesRaw = data && Array.isArray(data.linked_files) ? data.linked_files : [];
  const slidesRaw = data && Array.isArray(data.slides) ? data.slides : [];

  const linked_files = linkedFilesRaw
    .map((file): PreslinkProcessSafeLinkedFile | null => {
      const record = asRecord(file);
      if (!record) return null;
      const linkKey = readString(record, "link_key");
      if (!linkKey) return null;
      const fileName = readString(record, "file_name");
      return {
        link_key: linkKey,
        ...(fileName ? { file_name: fileName } : {}),
        byteLength: base64ByteLength(readString(record, "zip_base64") ?? ""),
      };
    })
    .filter((file): file is PreslinkProcessSafeLinkedFile => Boolean(file));

  const slides = slidesRaw
    .map((slide): PreslinkProcessSlide | null => {
      const slideRecord = asRecord(slide);
      const slideId = slideRecord ? readString(slideRecord, "slide_id") : undefined;
      if (!slideRecord || !slideId) return null;
      const shapesRaw = Array.isArray(slideRecord.shapes) ? slideRecord.shapes : [];
      return {
        slide_id: slideId,
        shapes: shapesRaw
          .map((shape): PreslinkProcessShape | null => {
            const shapeRecord = asRecord(shape);
            const shapeId = shapeRecord ? readString(shapeRecord, "shape_id") : undefined;
            if (!shapeRecord || !shapeId) return null;
            const linkKeys = Array.isArray(shapeRecord.link_keys)
              ? shapeRecord.link_keys.filter((linkKey): linkKey is string => typeof linkKey === "string")
              : [];
            return { shape_id: shapeId, link_keys: linkKeys };
          })
          .filter((shape): shape is PreslinkProcessShape => Boolean(shape)),
      };
    })
    .filter((slide): slide is PreslinkProcessSlide => Boolean(slide));

  return {
    errors: Array.isArray(root.errors) ? root.errors : [],
    ...(root.download_urls !== undefined ? { download_urls: root.download_urls } : {}),
    ...(data
      ? {
          data: {
            ...(presentation
              ? (() => {
                  const presentationId = readString(presentation, "presentation_id");
                  const fileName = readString(presentation, "file_name");
                  return {
                    presentation: {
                      ...(presentationId ? { presentation_id: presentationId } : {}),
                      ...(fileName ? { file_name: fileName } : {}),
                      archiveByteLength: base64ByteLength(readString(presentation, "zip_base64") ?? ""),
                    },
                  };
                })()
              : {}),
            linked_files,
            slides,
          },
        }
      : {}),
  };
}

/** Read a response body as JSON, falling back to raw text (or null when empty). */
async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** True when the response advertises an SSE (`text/event-stream`) body. */
function isEventStreamResponse(response: Response): boolean {
  return (response.headers.get("content-type") || "").toLowerCase().includes("text/event-stream");
}

/** Normalize CRLF/CR line endings to LF so SSE block parsing is consistent. */
function normalizeSseLineEndings(value: string): string {
  return value.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/**
 * Parse a single SSE block into a PresLink event, honoring `event:` and `data:`
 * fields. Returns null for comments, keep-alives, `[DONE]`, or unparseable data.
 */
function parseSseBlock(block: string): PreslinkProcessEvent | null {
  const dataLines: string[] = [];
  let eventType = "";

  for (const rawLine of block.split("\n")) {
    if (!rawLine || rawLine.startsWith(":")) continue;
    const colonIndex = rawLine.indexOf(":");
    const field = colonIndex >= 0 ? rawLine.slice(0, colonIndex) : rawLine;
    let value = colonIndex >= 0 ? rawLine.slice(colonIndex + 1) : "";
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") {
      eventType = value;
    } else if (field === "data") {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) return null;
  const dataText = dataLines.join("\n").trim();
  if (!dataText || dataText === "[DONE]") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataText);
  } catch {
    return null;
  }

  const record = asRecord(parsed);
  if (!record) return null;
  const parsedType = readString(record, "type");
  const type = parsedType || eventType;
  if (!type) return null;

  const eventData = asRecord(record.data);
  return {
    type,
    ...(readString(record, "message") ? { message: readString(record, "message") } : {}),
    ...(readString(record, "code") ? { code: readString(record, "code") } : {}),
    ...(readString(record, "error") ? { error: readString(record, "error") } : {}),
    ...(record.data === null ? { data: null } : eventData ? { data: eventData } : {}),
    ...(typeof record.timestamp_ms === "number" ? { timestamp_ms: record.timestamp_ms } : {}),
    ...(typeof record.excel_file === "string" ? { excel_file: record.excel_file } : {}),
  };
}

/** Convert a terminal `processing_complete` SSE event into a success response body. */
function buildSuccessfulBodyFromSseTerminalEvent(event: PreslinkProcessEvent): Record<string, unknown> {
  const terminalData = asRecord(event.data) ?? {};
  return {
    ...terminalData,
    ...(terminalData.data === undefined ? { data: terminalData } : {}),
    // Treat terminal processing_complete events as success even when warnings are present.
    errors: [],
  };
}

/** True when a value is one of the shared OAuth errors that requires reconnect. */
function isReauthRequiredOAuthCode(value: unknown): value is string {
  return (
    typeof value === "string" &&
    REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(value as never)
  );
}

/** Extract a human-readable error message from a `processing_error` SSE event. */
function readProcessingErrorMessage(event: PreslinkProcessEvent): string {
  const data = asRecord(event.data) ?? {};
  const dataError = readString(data, "error");
  return (
    readString(data, "message") ||
    (!isReauthRequiredOAuthCode(dataError) ? dataError : undefined) ||
    event.message ||
    (!isReauthRequiredOAuthCode(event.error) ? event.error : undefined) ||
    "PresLink process stream failed."
  );
}

/** Read a stable OAuth error code from the common PresLink SSE event shapes. */
function readProcessingOAuthErrorCode(event: PreslinkProcessEvent): string | undefined {
  const data = asRecord(event.data) ?? {};
  const candidates = [
    event.code,
    event.error,
    readString(data, "code"),
    readString(data, "error_code"),
    readString(data, "errorCode"),
    readString(data, "error"),
  ];
  return candidates.find(isReauthRequiredOAuthCode);
}

/**
 * Recognize legacy PresLink refresh failures that carry only human-readable
 * text. Keep the match constrained to Office/FactSet refresh context so an
 * unrelated processing 401 does not open the FactSet reconnect flow.
 */
function looksLikePreslinkFactSetAuthFailure(message: string): boolean {
  const hasRefreshContext =
    /\b(?:factset|oauth|office[-\s]?refresh|refresh(?:ed|ing)?)\b/i.test(message);
  if (!hasRefreshContext) return false;
  return (
    /\b(?:http\s*)?(?:401|403)\b/i.test(message) ||
    /\b(?:unauthorized|forbidden)\b/i.test(message) ||
    /\b(?:access\s+)?token\b.*\b(?:expired|invalid|missing|rejected)\b/i.test(message) ||
    /\b(?:authentication|authorization)\b.*\b(?:expired|invalid|missing|required|failed|rejected)\b/i.test(
      message
    )
  );
}

/** Convert a terminal PresLink processing event into a typed actionable error. */
function buildProcessingError(event: PreslinkProcessEvent): Error {
  const message = readProcessingErrorMessage(event);
  const structuredCode = readProcessingOAuthErrorCode(event);
  if (structuredCode || looksLikePreslinkFactSetAuthFailure(message)) {
    return new PreslinkReauthRequiredError(
      message,
      FACTSET_ROUTES.OAUTH_START,
      structuredCode || FACTSET_OAUTH_ERROR_CODES.EXPIRED
    );
  }
  return new Error(message);
}

/** Find the processed-presentation download URL in a response body, if present. */
function readDownloadUrlContainers(body: unknown): Array<Record<string, unknown>> {
  const root = asRecord(body) ?? {};
  const containers: Array<Record<string, unknown>> = [];
  const rootDownloadUrls = asRecord(root.download_urls);
  if (rootDownloadUrls) containers.push(rootDownloadUrls);
  const dataDownloadUrls = asRecord(asRecord(root.data)?.download_urls);
  if (dataDownloadUrls) containers.push(dataDownloadUrls);
  return containers;
}

/** Find the processed-presentation download URL in a response body, if present. */
function readPresentationDownloadUrl(body: unknown): string | undefined {
  for (const container of readDownloadUrlContainers(body)) {
    const presentationDownloadUrl = readString(container, "presentation");
    if (presentationDownloadUrl) return presentationDownloadUrl;
  }
  return undefined;
}

/** Best-effort extraction of a link key from a signed download URL query string. */
function readLinkKeyFromUrl(value: string): string | undefined {
  try {
    const url = new URL(value, globalThis.location?.href);
    return (
      url.searchParams.get("link_key") ||
      url.searchParams.get("linkKey") ||
      undefined
    );
  } catch {
    return undefined;
  }
}

/** True when a value looks like a direct download URL or rooted download path. */
function looksLikeDownloadUrl(value: string): boolean {
  return /^(https?:\/\/|\/)/i.test(value.trim());
}

interface LinkedFileDownloadDescriptor {
  linkKey: string;
  downloadUrl: string;
  fileName?: string;
}

/** Normalize one linked-file download descriptor from object or map forms. */
function normalizeLinkedFileDownloadDescriptor(
  value: unknown,
  fallbackLinkKey?: string
): LinkedFileDownloadDescriptor | null {
  if (typeof value === "string") {
    const downloadUrl = value.trim();
    const linkKey = (fallbackLinkKey || "").trim() || readLinkKeyFromUrl(downloadUrl) || "";
    if (!linkKey || !downloadUrl || !looksLikeDownloadUrl(downloadUrl)) return null;
    return { linkKey, downloadUrl };
  }
  const record = asRecord(value);
  if (!record) return null;
  const downloadUrl =
    readString(record, "url") ||
    readString(record, "download_url") ||
    readString(record, "downloadUrl") ||
    readString(record, "href") ||
    "";
  const linkKey =
    readString(record, "link_key") ||
    readString(record, "linkKey") ||
    (fallbackLinkKey || "").trim() ||
    readLinkKeyFromUrl(downloadUrl) ||
    "";
  if (!linkKey || !downloadUrl || !looksLikeDownloadUrl(downloadUrl)) return null;
  const fileName = readString(record, "file_name") || readString(record, "fileName");
  return { linkKey, downloadUrl, ...(fileName ? { fileName } : {}) };
}

/** Parse linked-workbook download descriptors from `download_urls` response fields. */
function readLinkedFileDownloadDescriptors(body: unknown): LinkedFileDownloadDescriptor[] {
  const byLinkKey = new Map<string, LinkedFileDownloadDescriptor>();
  const sourceKeys = [
    "linked_files",
    "linkedFiles",
    "workbooks",
    "workbook_files",
    "excel_files",
  ] as const;
  for (const container of readDownloadUrlContainers(body)) {
    for (const sourceKey of sourceKeys) {
      const raw = container[sourceKey];
      if (Array.isArray(raw)) {
        for (const entry of raw) {
          const descriptor = normalizeLinkedFileDownloadDescriptor(entry);
          if (!descriptor) continue;
          const existing = byLinkKey.get(descriptor.linkKey);
          if (!existing) {
            byLinkKey.set(descriptor.linkKey, descriptor);
          } else if (!existing.fileName && descriptor.fileName) {
            byLinkKey.set(descriptor.linkKey, { ...existing, fileName: descriptor.fileName });
          }
        }
      } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        for (const [mapKey, entry] of Object.entries(raw)) {
          const descriptor = normalizeLinkedFileDownloadDescriptor(entry, mapKey);
          if (!descriptor) continue;
          const existing = byLinkKey.get(descriptor.linkKey);
          if (!existing) {
            byLinkKey.set(descriptor.linkKey, descriptor);
          } else if (!existing.fileName && descriptor.fileName) {
            byLinkKey.set(descriptor.linkKey, { ...existing, fileName: descriptor.fileName });
          }
        }
      }
    }
  }
  return Array.from(byLinkKey.values());
}

/** Parse a filename from a Content-Disposition header (UTF-8 or plain form). */
function readFileNameFromContentDisposition(value: string | null): string | undefined {
  if (!value) return undefined;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim());
  const plainMatch = /filename="?([^";]+)"?/i.exec(value);
  return plainMatch?.[1]?.trim();
}

/** Derive a filename with an extension from a URL's path, if one is present. */
function readFileNameFromUrl(value: string): string | undefined {
  try {
    const url = new URL(value, globalThis.location?.href);
    const candidate = url.pathname.split("/").filter(Boolean).pop();
    return candidate && /\.[a-z0-9]+$/i.test(candidate) ? decodeURIComponent(candidate) : undefined;
  } catch {
    return undefined;
  }
}

/** Download a processed presentation from a signed URL and return its base64 + filename. */
async function fetchProcessedDownload(
  downloadUrl: string,
  downloadTypeLabel: string,
  options: { signal?: AbortSignal } = {}
): Promise<{ base64: string; fileName?: string }> {
  const normalizedUrl = downloadUrl.trim();
  const response = normalizedUrl.startsWith("/")
    ? await apiFetch(
        `${PRESLINK_DOWNLOAD_ENDPOINT}?path=${encodeURIComponent(normalizedUrl)}`,
        { method: "GET", signal: options.signal }
      )
    : await fetch(normalizedUrl, { credentials: "omit", signal: options.signal });
  if (!response.ok) {
    throw new Error(`Failed to download ${downloadTypeLabel} [status=${response.status}].`);
  }
  const base64 = arrayBufferToBase64(await response.arrayBuffer());
  const fileName =
    readFileNameFromContentDisposition(response.headers.get("content-disposition")) ||
    readFileNameFromUrl(normalizedUrl);
  return { base64, ...(fileName ? { fileName } : {}) };
}

/** Download a processed presentation from a signed URL and return its base64 + filename. */
async function fetchProcessedPresentationDownload(
  downloadUrl: string,
  options: { signal?: AbortSignal } = {}
): Promise<{ base64?: string; fileName?: string }> {
  return fetchProcessedDownload(downloadUrl, "processed presentation", options);
}

/**
 * Consume the PresLink SSE progress stream, forwarding events/messages to the
 * observers and resolving with the terminal success body. Throws on a
 * `processing_error` event or if the stream ends without a terminal event.
 */
export async function readPreslinkProcessEventStream(
  body: ReadableStream<Uint8Array>,
  options: Pick<PostPreslinkProcessPresentationFileOptions, "onEvent" | "onProgress"> = {}
): Promise<unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = "";
  let terminalBody: unknown = null;
  let streamError: Error | null = null;

  const handleEvent = (event: PreslinkProcessEvent): boolean => {
    try {
      options.onEvent?.(event);
    } catch {
      // Progress observers must never interrupt the transport stream.
    }
    if (event.message) {
      try {
        options.onProgress?.(event.message, event);
      } catch {
        // Progress observers must never interrupt the transport stream.
      }
    }

    if (event.type === "processing_complete") {
      terminalBody = buildSuccessfulBodyFromSseTerminalEvent(event);
      return true;
    }
    if (event.type === "processing_error") {
      streamError = buildProcessingError(event);
      return true;
    }
    return false;
  };

  const drainCompleteBlocks = (): boolean => {
    for (;;) {
      const blockEndIndex = sseBuffer.indexOf("\n\n");
      if (blockEndIndex < 0) return false;
      const block = sseBuffer.slice(0, blockEndIndex);
      sseBuffer = sseBuffer.slice(blockEndIndex + 2);
      const event = parseSseBlock(block);
      if (event && handleEvent(event)) return true;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += normalizeSseLineEndings(decoder.decode(value, { stream: true }));
      if (drainCompleteBlocks()) break;
    }
    sseBuffer += normalizeSseLineEndings(decoder.decode());
    const trailingEvent = parseSseBlock(sseBuffer.trim());
    if (!terminalBody && !streamError && trailingEvent) {
      handleEvent(trailingEvent);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Reader may already be released.
    }
  }

  if (streamError) {
    throw streamError;
  }
  if (!terminalBody) {
    throw new Error("PresLink process stream ended without a terminal event.");
  }
  return terminalBody;
}

/**
 * Pull the processed presentation archive out of a successful process response
 * so it can be re-opened into the deck. Unwraps a single-file Office archive if
 * the service wrapped the .pptx. Returns nothing when no archive is present.
 */
async function extractProcessedPresentation(
  body: unknown,
  options: { signal?: AbortSignal } = {}
): Promise<{ base64?: string; fileName?: string }> {
  const data = asRecord(asRecord(body)?.data);
  const presentation = data ? asRecord(data.presentation) : null;
  if (presentation) {
    const rawBase64 = readString(presentation, "zip_base64");
    if (rawBase64) {
      const fileName = readString(presentation, "file_name");
      const base64 = await unwrapSingleOfficeFileArchive(rawBase64, {
        rawEntryPrefix: "ppt/",
        fileExtensionPattern: /\.(pptx|pptm)$/i,
      });
      return { base64, ...(fileName ? { fileName } : {}) };
    }
  }
  const downloadUrl = readPresentationDownloadUrl(body);
  return downloadUrl ? fetchProcessedPresentationDownload(downloadUrl, options) : {};
}

/** Pull processed linked workbook payloads out of embedded data or download URLs. */
async function extractProcessedLinkedFiles(
  body: unknown,
  options: { signal?: AbortSignal } = {}
): Promise<PreslinkProcessOutputLinkedFile[]> {
  const data = asRecord(asRecord(body)?.data);
  const linkedFiles = data && Array.isArray(data.linked_files) ? data.linked_files : [];
  const byLinkKey = new Map<string, PreslinkProcessOutputLinkedFile>();
  for (const file of linkedFiles) {
    const record = asRecord(file);
    if (!record) continue;
    const linkKey = readString(record, "link_key");
    const zipBase64 = readString(record, "zip_base64");
    if (!linkKey || !zipBase64) continue;
    const fileName = readString(record, "file_name");
    byLinkKey.set(linkKey, {
      link_key: linkKey,
      zip_base64: zipBase64,
      ...(fileName ? { file_name: fileName } : {}),
    });
  }

  const downloadDescriptors = readLinkedFileDownloadDescriptors(body);
  for (const descriptor of downloadDescriptors) {
    const existing = byLinkKey.get(descriptor.linkKey);
    if (existing) {
      if (!existing.file_name && descriptor.fileName) {
        byLinkKey.set(descriptor.linkKey, { ...existing, file_name: descriptor.fileName });
      }
      continue;
    }
    const downloaded = await fetchProcessedDownload(
      descriptor.downloadUrl,
      `processed linked workbook (${descriptor.linkKey})`,
      options
    );
    byLinkKey.set(descriptor.linkKey, {
      link_key: descriptor.linkKey,
      zip_base64: downloaded.base64,
      ...(descriptor.fileName || downloaded.fileName
        ? { file_name: descriptor.fileName || downloaded.fileName }
        : {}),
    });
  }

  return Array.from(byLinkKey.values());
}

/**
 * Send a process-presentation request to the server proxy, consuming SSE
 * progress when streamed. Returns the model-safe summary plus the raw processed
 * presentation and linked-workbook payloads. Throws on a non-OK response.
 */
export async function postPreslinkProcessPresentationFile(
  request: PreslinkProcessPresentationRequest,
  options: PostPreslinkProcessPresentationFileOptions = {}
): Promise<PreslinkProcessResult> {
  const requestSummary = summarizePreslinkProcessRequest(request);
  console.info("[PresLink] process request", requestSummary);
  const response = await apiFetch(PRESLINK_PROCESS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(request),
    signal: options.signal,
  });

  const body =
    response.ok && response.body && isEventStreamResponse(response)
      ? await readPreslinkProcessEventStream(response.body, {
          onEvent: options.onEvent,
          onProgress: options.onProgress,
        })
      : await parseResponseBody(response);
  const responseSummary = summarizePreslinkProcessResponse(body);
  if (!response.ok) {
    const responseDiagnostic = {
      status: response.status,
      ok: response.ok,
      contentType: response.headers.get("content-type") || "",
      bodyType: body === null ? "null" : Array.isArray(body) ? "array" : typeof body,
      errorCount: responseSummary.errors.length,
      hasDownloadUrls: responseSummary.download_urls !== undefined,
      outputLinkedFileCount: responseSummary.data?.linked_files.length ?? 0,
    };
    console.warn("[PresLink] process response failed", {
      ...responseDiagnostic,
      body,
    });
    const errorBody = asRecord(body);
    const errorCode =
      errorBody && typeof errorBody.error === "string" ? String(errorBody.error) : "";
    const message =
      typeof body === "string"
        ? body
        : errorBody && typeof errorBody.message === "string"
          ? String(errorBody.message)
          : `PresLink process API failed with status ${response.status}.`;
    if (REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(errorCode as never)) {
      const reauthorizationUrl =
        errorBody && typeof errorBody.reauthorizationUrl === "string"
          ? String(errorBody.reauthorizationUrl)
          : FACTSET_ROUTES.OAUTH_START;
      throw new PreslinkReauthRequiredError(message, reauthorizationUrl, errorCode);
    }
    throw new Error(`${message} [status=${response.status}]`);
  }

  const processedPresentation = await extractProcessedPresentation(body, { signal: options.signal });
  const processedLinkedFiles = await extractProcessedLinkedFiles(body, { signal: options.signal });
  return {
    summary: responseSummary,
    ...(processedPresentation.base64 ? { processedPresentationBase64: processedPresentation.base64 } : {}),
    ...(processedPresentation.fileName ? { processedPresentationFileName: processedPresentation.fileName } : {}),
    ...(processedLinkedFiles.length > 0 ? { processedLinkedFiles } : {}),
  };
}
